import { useApolloClient, gql } from '@apollo/client';
import { useEffect, useState } from 'react';
import NoData from 'src/assets/svg/quality.svg';
import {
	Table,
	Tag,
	Modal,
	Button,
	Popover,
	ConfigProvider,
	Empty,
} from 'antd';
import { TeamOutlined } from '@ant-design/icons';

const QUERY_BADGE_ASSIGNMENTS_BY_SENDER = gql`
	query QueryBadgeAssignmentsBySender(
		$awardedBy: ID!
		$limit: Int
		$nextToken: String
	) {
		queryBadgeAssignmentsBySender(
			awardedBy: $awardedBy
			limit: $limit
			nextToken: $nextToken
		) {
			items {
				id
				accountClaimId
				accountClaim {
					id
					firstName
					lastName
				}
				awardedBy
				awardedByUser {
					id
					firstName
					lastName
				}
				recipients {
					user {
						id
						firstName
						lastName
					}
					accountClaim {
						id
						firstName
						lastName
					}
				}
				approvalStatus
				declineReason
				quietDecline
				createdAt
				content {
					message
					gifURL
					tags
				}
				badge {
					id
					name
				}
				user {
					id
					firstName
					lastName
				}
			}
			nextToken
		}
	}
`;

const MySentActivityComponent = ({ currentUser }) => {
	const client = useApolloClient();
	const [sentAssignments, setSentAssignments] = useState([]);
	const [currentToken, setCurrentToken] = useState(null);
	const [loading, setLoading] = useState(false);
	const [messageModalVisible, setMessageModalVisible] = useState(false);
	const [messageModalContent, setMessageModalContent] = useState({
		message: '',
		gifURL: '',
	});
	const [adminNoteModalVisible, setAdminNoteModalVisible] = useState(false);
	const [adminNoteContent, setAdminNoteContent] = useState('');

	useEffect(() => {
		queryBadgeAssignmentsBySender();
	}, []);

	const queryBadgeAssignmentsBySender = async () => {
		if (loading) return;
		setLoading(true);
		try {
			const { data } = await client.query({
				query: QUERY_BADGE_ASSIGNMENTS_BY_SENDER,
				variables: {
					awardedBy: currentUser.id,
					limit: 10,
					nextToken: currentToken,
				},
			});
			const results = data?.queryBadgeAssignmentsBySender || {
				items: [],
				nextToken: null,
			};
			setSentAssignments([...sentAssignments, ...results.items]);
			setCurrentToken(results.nextToken);
			setLoading(false);
		} catch (error) {
			console.error(error);
			setLoading(false);
		}
	};

	const handleViewMessage = (content) => {
		setMessageModalContent(content);
		setMessageModalVisible(true);
	};

	const handleViewAdminNote = (declineReason) => {
		setAdminNoteContent(declineReason);
		setAdminNoteModalVisible(true);
	};

	const columns = [
		{
			title: 'Date',
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: 110,
			render: (date) => new Date(date).toLocaleDateString(),
		},
		{
			title: 'Recipient',
			key: 'recipient',
			width: 150,
			render: (_, record) => {
				const firstRecipient =
					record?.recipients?.[0]?.user ||
					record?.recipients?.[0]?.accountClaim ||
					record?.user ||
					record?.accountClaim;
				const recipients = record?.recipients || [];
				const mappedRecipients = recipients.map((recipient) => {
					if (recipient.user) {
						return {
							id: recipient.user?.id,
							firstName: recipient.user?.firstName,
							lastName: recipient.user?.lastName,
							emailAddress: recipient.user?.emailAddress,
						};
					} else if (recipient.accountClaim) {
						return {
							id: 'account_claim',
							firstName: recipient.accountClaim?.firstName,
							lastName: recipient.accountClaim?.lastName,
							emailAddress: recipient.accountClaim?.emailAddress,
						};
					}
				});

				const withPopover = (
					<Popover
						content={
							<div
								style={{
									maxWidth: 400,
									maxHeight: 300,
									overflowY: 'scroll',
									marginBottom: 0,
								}}
							>
								<span
									style={{
										fontWeight: 800,
										color: 'rgba(0, 0, 0, 0.85)',
									}}
								>
									All Recipients:
								</span>
								<ul style={{ marginBottom: 0 }}>
									{mappedRecipients.length > 0 && (
										<>
											{mappedRecipients.map((re) => {
												const recipientString = `${re.firstName} ${re.lastName}`;
												return <li key={re.id}>{recipientString}</li>;
											})}
										</>
									)}
								</ul>
							</div>
						}
						getPopupContainer={(triggerNode) => triggerNode.parentNode}
					>
						<span
							style={{
								fontSize: '14px',
								color: 'var(--lochmara)',
								cursor: 'help',
							}}
						>
							<span
								style={{
									fontSize: '14px',
									color: 'var(--lochmara)',
									cursor: 'help',
									marginLeft: '8px',
								}}
							>
								<TeamOutlined />
							</span>
						</span>
					</Popover>
				);
				if (firstRecipient) {
					return (
						<>
							<span>{`${firstRecipient?.firstName} ${firstRecipient?.lastName}`}</span>
							{mappedRecipients?.length > 1 && withPopover}
						</>
					);
				}
				return <span>–</span>;
			},
		},
		{
			title: 'Status',
			dataIndex: 'approvalStatus',
			key: 'approvalStatus',
			width: 90,
			render: (approvalStatus) => {
				return (
					<Tag
						color={
							approvalStatus === 'Approved'
								? 'green'
								: approvalStatus === 'Declined'
									? 'red'
									: 'blue'
						}
						style={{ marginRight: 0 }}
					>
						{approvalStatus}
					</Tag>
				);
			},
		},
		{
			title: 'Recognition',
			dataIndex: ['badge', 'name'],
			key: 'recognition',
			render: (_, record) => {
				if (record?.badge?.name) {
					return <span>{record.badge.name}</span>;
				}
				return <span>–</span>;
			},
		},
		{
			title: 'Message',
			key: 'message',
			width: 90,
			render: (_, record) => {
				if (record?.content?.message || record?.content?.gifURL) {
					return (
						<Button
							type="link"
							onClick={() => handleViewMessage(record.content)}
						>
							View
						</Button>
					);
				}
				return <span>–</span>;
			},
		},
		{
			title: 'Decline Reason',
			dataIndex: 'declineReason',
			key: 'adminNote',
			render: (declineReason) => {
				if (declineReason) {
					return (
						<Button
							type="link"
							onClick={() => handleViewAdminNote(declineReason)}
						>
							View
						</Button>
					);
				}
				return <span>–</span>;
			},
		},
	];

	return (
		<div style={{ height: '100%', minHeight: 360 }}>
			<ConfigProvider
				renderEmpty={() =>
					loading ? (
						<div
							style={{
								height: '284px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								flexDirection: 'column',
							}}
						></div>
					) : (
						<Empty
							style={{
								height: '284px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								flexDirection: 'column',
							}}
							className="recognition-empty"
							image={
								<div
									style={{
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
									}}
									className="my-sent-activity-empty"
								>
									<img src={NoData} alt="img"></img>
								</div>
							}
							description={
								<div
									className="no-badges-state"
									style={{
										textAlign: 'center',
										padding: '0px 20px',
										color: '#666',
									}}
								>
									<h3
										style={{
											margin: '0 0 8px 0',
											fontSize: '18px',
											fontWeight: '600',
											color: '#333',
										}}
									>
										No Sent Recognitions Yet
									</h3>
									<p style={{ margin: '0', fontSize: '14px', color: '#595959' }}>
										Your sent activity will appear here once you send a
										Recognition.
									</p>
								</div>
							}
						/>
					)
				}
			>
				<Table
					columns={columns}
					dataSource={sentAssignments}
					rowKey="id"
					loading={loading}
					pagination={false}
					scroll={{
						x: 645,
						y: 360,
					}}
					style={{ minHeight: 360 }}
				/>
			</ConfigProvider>
			<div style={{ marginTop: 16, textAlign: 'center' }}>
				<Button
					type="Link"
					disabled={!currentToken}
					onClick={queryBadgeAssignmentsBySender}
					loading={loading}
					style={{
						color: currentToken
							? 'var(--primary-btn-color, var(--theme-primary))'
							: '#ddd',
						height: 'auto',
						padding: 0,
						border: 'none',
						display: 'inline-flex',
						alignItems: 'center',
						marginRight: '10px',
						boxShadow: 'none',
						background: currentToken ? '' : 'transparent',
					}}
				>
					Load More
				</Button>
			</div>

			<Modal
				title="Message"
				open={messageModalVisible}
				onCancel={() => setMessageModalVisible(false)}
				footer={[
					<Button key="close" onClick={() => setMessageModalVisible(false)}>
						Close
					</Button>,
				]}
			>
				<div>
					{messageModalContent.message && <p>{messageModalContent.message}</p>}
					{messageModalContent.gifURL && (
						<img
							src={messageModalContent.gifURL}
							alt="GIF"
							style={{ maxWidth: '100%' }}
						/>
					)}
				</div>
			</Modal>

			<Modal
				title="Decline Reason"
				open={adminNoteModalVisible}
				onCancel={() => setAdminNoteModalVisible(false)}
				footer={[
					<Button key="close" onClick={() => setAdminNoteModalVisible(false)}>
						Close
					</Button>,
				]}
			>
				<p>{adminNoteContent}</p>
			</Modal>
		</div>
	);
};

export default MySentActivityComponent;
